import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { loader } from "@monaco-editor/react";
import { IDisposable } from "monaco-editor";

import CustomModal from "shared/components/CustomModal";
import * as S from "@styled";
import colors from "shared/styles/colors";
import { languageProvider } from "shared/monaco/lang/languageProvider";
import { ICompletionTypes } from "shared/monaco/model/model";

import { ProcessVariableValueEditor } from "processEditor/components/ProcessVariablesPanel";
import { ProcessVariableEdit } from "processList/model/ProcessVariableEdit";
import { getProcessVariableValue, getProcessVariableDisplayValue } from "processEditor/reducers/ProcessVariablesReducer";
import { getErrorsOfType } from "processList/components/ProcessVariablesEditorModal";
import { ProcessVarValidationErrorType } from "../../../processEditor/actions/ValidateProcessVariables";
import { validateProcessVariableValue } from "../../../processEditor/actions/ValidateProcessVariables";

import { AppContext } from "../../App";
import { ProcessTemplateAppReducerState } from "processTemplates/model/AppState";
import { createProcessFromTemplate, setCreateModalOpen } from "processTemplates/actions/AppActions";
import { StyleModalContentLabel } from "./EditModeModal";
import { IProcessVariable } from "processEditor/model/IProcessData";
import { Utilities } from "shared/utilities";
import { CreateEnvironmentVariableEditorContent } from "processEditor/components/CreateEnvironmentVariableEditor";
import { SchemaType } from "environmentVariables/model/VariableSchema";
import { ValidationError } from "processEditor/model/ValidationData";
import { Checkbox } from "shared/components/Checkbox";

enum CreateProcessPhase {
    CONFIG = 0,
    PROCESSVARIABLES,
    ENVIRONMENTVARIABLES,
}

export const getInitialProcessVariables = (processVariables?: IProcessVariable[]): ProcessVariableEdit[] | undefined => {
    return processVariables ? processVariables.map((p, i) => (
        {
            ...p,
            displayValue: getProcessVariableDisplayValue(p.value, p.mode),
            originalValue: p.value,
            validationErrors: [],
            id: i
        })) : undefined;
};

const CreateModal: React.FC = () => {
    const allowEmptyProcessVariable = false;
    const skipEnvVarValidationInProcessVariablePhase = true; // Because the user is prompted to create the missing env vars in the env vars phase

    const { state, dispatch } = React.useContext<ProcessTemplateAppReducerState>(AppContext);
    const [currentPhase, setCurrentPhase] = useState(CreateProcessPhase.CONFIG);
    const [processName, setProcessName] = useState<string>('');
    const [processDescription, setProcessDesription] = useState('');
    const [processVariables, setProcessVariables] = useState<ProcessVariableEdit[]>();
    const [environmentVariables, setEnvironmentVariables] = useState<string[]>();
    const [allExistingEnvironmentVariables, setAllExistingEnvironmentVariables] = useState<string[]>();
    const [ignoreProcessTags, setIgnoreProcessTags] = useState<boolean>(false);

    useEffect(() => {
        if (state.templateForCreate) {
            setProcessVariables(getInitialProcessVariables(state.templateForCreate?.processVariables));
            setEnvironmentVariables(state.templateForCreate?.environmentVariables);
        } else {
            setCurrentPhase(CreateProcessPhase.CONFIG);
            setProcessName('');
            setProcessDesription('');
            setProcessVariables(undefined);
            setEnvironmentVariables(undefined);
        }
    }, [state.templateForCreate]);

    useEffect(() => {
        if ((processVariables || environmentVariables) && !allExistingEnvironmentVariables) {
            (async () => {
                const envVars: string[] = await Utilities.getFetch('api/environmentVariable/keys');
                setAllExistingEnvironmentVariables(envVars);
            })();
        }
    }, [processVariables, environmentVariables]);

    const handleClose = () => {
        setCreateModalOpen(dispatch, undefined);
    };

    const handleProcessVariableChange = (id: number, newValues: string) => {
        if (!processVariables) {
            return;
        }

        const changedVariableIdx = processVariables.findIndex((pv) => pv.id === id);
        if (0 <= changedVariableIdx) {
            const changedVariable = processVariables[changedVariableIdx];
            changedVariable.value = getProcessVariableValue(newValues, changedVariable.mode);
            changedVariable.displayValue = newValues;
            changedVariable.validationErrors = validateProcessVariableValue(changedVariable, [], allowEmptyProcessVariable, skipEnvVarValidationInProcessVariablePhase)
            const changedVariables = [...processVariables];
            changedVariables[changedVariableIdx] = changedVariable;
            setProcessVariables(changedVariables);
        }
    };

    const getContinueButtonText = () => {
        if (currentPhase === CreateProcessPhase.CONFIG && processVariables?.length || currentPhase === CreateProcessPhase.PROCESSVARIABLES && environmentVariables?.length) {
            return 'Continue';
        }
        return 'Create';
    };

    const handleCreate = () => {
        if (state.templateForCreate?.id) {
            createProcessFromTemplate(dispatch, state.templateForCreate.id, {
                name: processName,
                description: processDescription,
                processVariables,
                environmentVariables,
                ignoreProcessTags
            });
        }
    };

    const handleContinueButtonClick = () => {
        if (processName === '') {
            return;
        }

        if (!processVariables?.length && !environmentVariables?.length) {
            handleCreate();
            return;
        }

        if (currentPhase === CreateProcessPhase.CONFIG && !processVariables?.length && environmentVariables?.length) {
            setCurrentPhase(CreateProcessPhase.ENVIRONMENTVARIABLES);
            return;
        }

        if (currentPhase === CreateProcessPhase.PROCESSVARIABLES) {
            const validatedProcessVariables = processVariables?.map(processVariable => ({
                ...processVariable,
                validationErrors: validateProcessVariableValue(processVariable, [], allowEmptyProcessVariable, skipEnvVarValidationInProcessVariablePhase)
            }));

            setProcessVariables(validatedProcessVariables);

            if (validatedProcessVariables?.some(pv => pv.validationErrors.length > 0)) {
                return;
            }
        }

        if (currentPhase === CreateProcessPhase.PROCESSVARIABLES && !GetAllReferencedEnvironmentVariables(environmentVariables ?? [], processVariables ?? []).length) {
            handleCreate();
            return;
        }

        if (currentPhase === CreateProcessPhase.ENVIRONMENTVARIABLES) {
            handleCreate();
            return;
        }

        setCurrentPhase((prev) => prev + 1);
    };

    const handleBackButtonClick = () => {
        if (currentPhase === CreateProcessPhase.ENVIRONMENTVARIABLES && !processVariables) {
            setCurrentPhase(CreateProcessPhase.CONFIG);
            return;
        }
        setCurrentPhase((prev) => prev - 1);
    };

    return (
        <CustomModal
            label="template-create-modal"
            size="medium"
            onClose={handleClose}
            shouldCloseOnOverlayClick={true}
            show={!!state.templateForCreate}
            modalTitle={<h2>Template Process configuration wizard</h2>}
        >
            <>
                <h1>
                    {state.templateForCreate?.name}
                </h1>

                <ProcessConfigurationPhaseDecoration
                    currentPhase={currentPhase}
                    hasProcessVariables={!!processVariables && processVariables.length > 0}
                    hasEnvironmentVariables={GetAllReferencedEnvironmentVariables(environmentVariables ?? [], processVariables ?? []).length > 0}
                />

                <StyledCreateProcessForm
                    id="create-process-form"
                    onSubmit={(e) => e.preventDefault()}
                >
                    {currentPhase === CreateProcessPhase.CONFIG && (
                        <ConfigPhaseFormContent
                            processName={processName}
                            onProcessNameChange={setProcessName}
                            processDescription={processDescription}
                            onProcessDescriptionChange={setProcessDesription}
                            processTags={state.templateForCreate?.processTags}
                            ignoreProcessTags={ignoreProcessTags}
                            handleIgnoreProcessTags={() => setIgnoreProcessTags(prev => !prev)}
                        />
                    )}

                    {currentPhase === CreateProcessPhase.PROCESSVARIABLES && processVariables && (
                        <ProcessVariablesPhaseFormContent
                            processVariables={processVariables}
                            onProcessVariableChange={handleProcessVariableChange}
                            allExistingEnvironmentVariables={allExistingEnvironmentVariables}
                        />
                    )}

                    {currentPhase === CreateProcessPhase.ENVIRONMENTVARIABLES && environmentVariables && (
                        <EnvironmentVariablesPhaseFormContent
                            staticEnvironmentVariables={environmentVariables}
                            processVariables={processVariables ?? []}
                            allExistingEnvironmentVariables={allExistingEnvironmentVariables}
                        />
                    )}
                </StyledCreateProcessForm>

                <S.FlexContainer
                    justifyEnd={0 === currentPhase}
                    justifySpaceBetween={0 < currentPhase}
                    style={{ marginTop: '38px ' }}
                >

                    {CreateProcessPhase.CONFIG < currentPhase && (
                        <S.Button
                            variant={S.ButtonType.bordered}
                            onClick={handleBackButtonClick}
                        >
                            Back
                        </S.Button>
                    )}

                    <S.Button
                        type="submit"
                        form="create-process-form"
                        variant={S.ButtonType.bordered}
                        onClick={handleContinueButtonClick}
                    >
                        {getContinueButtonText()}
                    </S.Button>
                </S.FlexContainer>
            </>
        </CustomModal>
    );
};

export default CreateModal;

interface IProcessConfigurationPhaseDecorationProps {
    currentPhase: number;
    hasProcessVariables: boolean;
    hasEnvironmentVariables: boolean;
}

const ProcessConfigurationPhaseDecoration: React.FC<IProcessConfigurationPhaseDecorationProps> = ({
    currentPhase,
    hasProcessVariables,
    hasEnvironmentVariables
}) => {
    if (!hasProcessVariables && !hasEnvironmentVariables) {
        return null;
    }

    const getPhaseDecorationGridTemplateColumns = () => {
        return (hasProcessVariables && hasEnvironmentVariables) ? 'auto 1fr auto 1fr auto' : 'auto 1fr auto';
    };

    const getPhaseDecorationSpanClassName = (curPhase: number, cutOffPhase: number) => {
        if (cutOffPhase < curPhase) {
            return undefined;
        } else if (cutOffPhase === curPhase) {
            return 'bold';
        } else {
            return 'grey';
        }
    };

    return (
        <StyledProcessConfigurationPhaseDecoration style={{ gridTemplateColumns: getPhaseDecorationGridTemplateColumns() }}>
            <span className={getPhaseDecorationSpanClassName(currentPhase, CreateProcessPhase.CONFIG)}>
                Configure Process
            </span>

            {hasProcessVariables && (
                <>
                    <div>
                        &nbsp;
                    </div>

                    <span className={getPhaseDecorationSpanClassName(currentPhase, CreateProcessPhase.PROCESSVARIABLES)}>
                        Configure Variables
                    </span>
                </>
            )}

            {hasEnvironmentVariables && (
                <>
                    <div>
                        &nbsp;
                    </div>

                    <span className={getPhaseDecorationSpanClassName(currentPhase, CreateProcessPhase.ENVIRONMENTVARIABLES)} >
                        Environment variables
                    </span>
                </>
            )}
        </StyledProcessConfigurationPhaseDecoration>
    );
};

const StyledProcessConfigurationPhaseDecoration = styled.div`
    margin: 25px 0;
    display: grid;
    align-items: center;
    gap: 24px;

    * {
        font-size: 14px;
    }
    
    span.bold {
        font-family: 'Gilroy-Bold';
    }

    span.grey {
        color: ${colors.grey_1};
    }

    div {
        height: 1px;
        background-color: ${colors.grey_2};
    }
`;

const StyledCreateProcessForm = styled.form`
    padding: 16px 24px;
    display: grid;
    gap: 21px;

    fieldset {
        margin: 0;
        border: 0;
        padding: 0;

        legend {
            font-size: 20px;
            font-family: 'Gilroy-Medium';
        }

        > div {
            margin-top: 21px;

            > div:nth-child(2) {
                margin: 0;
            }
        }
    }  
`;

interface IConfigPhaseFormContentProps {
    processName: string;
    processDescription: string;
    processTags?: string[];
    ignoreProcessTags: boolean;
    handleIgnoreProcessTags: () => void;
    onProcessNameChange: (processName: string) => void;
    onProcessDescriptionChange: (processDescription: string) => void;
}

const ConfigPhaseFormContent: React.FC<IConfigPhaseFormContentProps> = (props) => {
    const [userHasAccessToAllProcessTags, setUserHasAccessToAllTags] = useState<boolean>();

    useEffect(() => {
        const checkProcessTags = async () => {
            try {
                const hasAccessToAllTags = await Utilities.getFetch<boolean>("api/tag/isUserAllowedToAccessTags", { tagsToCheck: props.processTags });
                setUserHasAccessToAllTags(hasAccessToAllTags);
            } catch (e) {
                console.error("Failed to validate Process tags", e);
            }
        }
        checkProcessTags();
    }, [props.processTags]);

    return (
        <>
            <StyleModalContentLabel>
                <span>
                    Process name
                </span>

                <S.Input
                    id="process-name-input"
                    type="text"
                    required
                    maxLength={512}
                    value={props.processName}
                    onChange={(e) => props.onProcessNameChange(e.currentTarget.value)}
                    style={{ width: '100%' }}
                    autoComplete="off"
                />
            </StyleModalContentLabel>

            <StyleModalContentLabel>
                <span>
                    Process description
                </span>

                <S.TextArea
                    id="process-description-input"
                    value={props.processDescription}
                    onChange={(e) => props.onProcessDescriptionChange(e.currentTarget.value)}
                />

            </StyleModalContentLabel>

            {props.processTags && props.processTags.length > 0 && (
                <StyleModalContentLabel>
                    <span>
                        Process tags
                    </span>

                    <S.FlexContainer style={{ padding: "10px 0" }}>
                        <Checkbox
                            name={"ignoreProcessTags"}
                            onChange={props.handleIgnoreProcessTags}
                            checked={props.ignoreProcessTags}
                        />
                        <span style={{ all: 'unset' }}>
                            Ignore Process tags
                        </span>
                    </S.FlexContainer>

                    {userHasAccessToAllProcessTags !== undefined && !userHasAccessToAllProcessTags ? (
                        <S.WarningContainer>
                            <div className="alert-warning" id="userDoesntHaveAccessToAllProcessTags">
                                <div className="alert-label">{FRENDS.Localizations.Warning}</div>
                                <div className="alert-content">You do not have access to all Process tags and you will not be able to access the Process after its creation. You can ignore Process tags to create the Process without them.</div>
                            </div>
                        </S.WarningContainer>
                    ) : (
                        <S.FlexContainer>
                            {props.processTags.map((t, i) => (
                                <S.FlexItem key={i} flex="0 1 auto" style={{ paddingRight: "15px" }}>
                                    <S.ProcessTag>
                                        <S.ProcessTagHole />
                                        {t}
                                    </S.ProcessTag>
                                </S.FlexItem>
                            ))}
                        </S.FlexContainer>
                    )}
                </StyleModalContentLabel>
            )}
        </>
    )
}

interface IProcessVariablesPhaseFormContentProps {
    processVariables: ProcessVariableEdit[];
    onProcessVariableChange: (id: number, newValue: string) => void;
    allExistingEnvironmentVariables?: string[];
}

const ProcessVariablesPhaseFormContent: React.FC<IProcessVariablesPhaseFormContentProps> = ({
    processVariables,
    onProcessVariableChange,
    allExistingEnvironmentVariables,
}) => {
    const mountedRef = React.useRef(false);
    const disposableLanguageProviderRef = React.useRef<IDisposable | null>(null);

    const registerLanguageProvider = async () => {
        const initializedMonaco = await loader.init();

        const completionTypes: ICompletionTypes = {
            environmentVariables: allExistingEnvironmentVariables || [],
            processReferences: [],
            returnVariables: [],
            triggers: [],
            variables: [],
            snippets: [],
            processVariables: [],
            apiSpecPaths: [],
        }

        disposableLanguageProviderRef.current = languageProvider.registerLanguageProvider(initializedMonaco, completionTypes);
    };

    const disposeLanguageProvider = () => {
        disposableLanguageProviderRef.current?.dispose();
        disposableLanguageProviderRef.current = null;
    };

    React.useEffect(() => {
        if (!mountedRef.current) {
            registerLanguageProvider();
        }

        mountedRef.current = true;

        return () => disposeLanguageProvider();
    }, []);

    return (
        <fieldset>
            <legend>
                Process variables
            </legend>

            {processVariables.map((pv, i) => {
                const valueErrors = getErrorsOfType(pv, ProcessVarValidationErrorType.Value);
                const referenceErrors = getErrorsOfType(pv, ProcessVarValidationErrorType.Reference);

                return (
                    <div key={i}>
                        <ProcessVariableValueEditor
                            label={pv.name}
                            processVariable={pv}
                            valueErrors={valueErrors}
                            referenceError={referenceErrors.length ? referenceErrors[0] : undefined}
                            updateVariableValue={onProcessVariableChange}
                            disableEdit={false}
                            onlyEditValue={true}
                            showModeSelector={true}
                            hideDescription
                        />

                        <p style={{ margin: '8px 0 0' }}>
                            {pv.description}
                        </p>
                    </div>
                );
            })}
        </fieldset>
    );
}

interface IEnvironmentVariablesPhaseFormContentProps {
    staticEnvironmentVariables: string[];
    processVariables: IProcessVariable[];
    allExistingEnvironmentVariables?: string[];
}

const EnvironmentVariablesPhaseFormContent: React.FC<IEnvironmentVariablesPhaseFormContentProps> = ({
    staticEnvironmentVariables: staticEnvironmentVariables,
    processVariables: processVariables,
    allExistingEnvironmentVariables
}) => {
    const [forbiddenEnvironmentVariableNames, setForbiddenEnvironmentVariableNames] = useState<string[]>();
    const [newEnvironmentVariable, setNewEnvironmentVariable] = useState<string>();
    const [allExistingEnvironmentVariablesSet, setAllExistingEnvironmentVariablesSet] = useState(new Set(allExistingEnvironmentVariables || ''));

    const handleCreateEnvVariableButtonClick = (ev: string) => {
        setNewEnvironmentVariable(ev);
        setAllExistingEnvironmentVariablesSet(new Set([...(allExistingEnvironmentVariables || []), ev]));
    };

    React.useEffect(() => {
        (async () => {
            if (!forbiddenEnvironmentVariableNames) {
                const forbiddenNames = await Utilities.getFetch<string[]>(`api/environmentVariable/keys`);
                setForbiddenEnvironmentVariableNames(forbiddenNames);
            }
        })();
    }, [forbiddenEnvironmentVariableNames]);

    const handleAddNewEnvironmentVariable = async (type: SchemaType, name: string, parentName: string | undefined, value: string | boolean | string[] | undefined) => {
        try {
            await Utilities.postJsonAndParseResponse("api/environmentVariable/addSchemaWithValue", {
                parentVariableName: parentName,
                name: name,
                type: type,
                value: value
            });
            setNewEnvironmentVariable(undefined);
        }
        catch (e) {
            console.error(e);
            Utilities.toastError(e);
        }
    }

    return (
        <fieldset>
            <legend>
                Used Environment variables
            </legend>

            {GetAllReferencedEnvironmentVariables(staticEnvironmentVariables, processVariables).map((ev, i) => (
                <S.FlexContainer
                    key={i}
                    gap={16}
                    centerItems
                >
                    <p style={{ margin: 0 }}>
                        <span>
                            {ev}
                        </span>
                        {!allExistingEnvironmentVariablesSet.has(ev) && (
                            <span> - missing</span>
                        )}
                    </p>

                    {!allExistingEnvironmentVariablesSet.has(ev) && (
                        <StyledCreateEnvVariableButton onClick={() => handleCreateEnvVariableButtonClick(ev)}>
                            Create
                        </StyledCreateEnvVariableButton>
                    )}
                    {newEnvironmentVariable === ev && (
                        <S.FlexItem flex="100%">
                            <CreateEnvironmentVariableEditorContent
                                addNewVariable={handleAddNewEnvironmentVariable}
                                error={new ValidationError(null, undefined, null, `.${ev}`)}
                                forbiddenNames={forbiddenEnvironmentVariableNames || []}
                                handleClose={() => setNewEnvironmentVariable(undefined)}
                            />
                        </S.FlexItem>
                    )}
                </S.FlexContainer>
            ))}
        </fieldset>
    );
};

const GetAllReferencedEnvironmentVariables = (staticEnvironmentVariables: string[], processVariables: IProcessVariable[]): string[] => {
    const envVarPrefix = "#env.";
    return Array.from(new Set(staticEnvironmentVariables.concat(processVariables.filter(pv => pv.value.toLowerCase().startsWith(envVarPrefix)).map(pv => pv.value.slice(envVarPrefix.length)))));
}

const StyledCreateEnvVariableButton = styled(S.Button)`
    padding: 0;
    height: initial;
    font-size: 16px;
    background-color: initial !important;
    text-decoration: underline;
`;